import { CoreTypes, DEFAULT_FONT_FAMILY, type Fill, type Id, type ObjectRecord, type Primitive, type Stroke } from '@vp/shared-types';
import { commands, type Rect } from '@vp/core';
import type { ImageProvider } from './backend';
import { drawPrimitive } from './canvas2d';
import { cssColor, parseColor } from './color';
import { arrowHead, dashPattern, trimForArrows } from './pathgeom';
import { patternSpacing, patternThickness } from './canvas2d';
import type { RenderItem, Scene } from './scene';
import { layoutText } from './text';
import { orientTris, roundStroke, strokeVariable } from './webgl/tessellate';

/** Image export (§13.1): PNG via the Canvas 2D path, SVG with vector primitives. */

export class ExportTooLargeError extends Error {
  readonly code = 'E_EXPORT_TOO_LARGE';
  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    super(`Export of ${width}×${height} exceeds the limit`);
  }
}

export interface ExportOptions {
  /** Objects to export (null = everything intersecting `bounds`). */
  ids: Id[] | null;
  /** World rect; computed from objects when omitted. */
  bounds?: Rect;
  padding?: number;
  scale?: number;
  /** CSS color, or null for transparent. */
  background: string | null;
  unavailable: 'placeholder' | 'skip';
  isUnavailable: (o: ObjectRecord) => boolean;
  images: ImageProvider;
  /** Resolve an image source to a data URL for SVG embedding. */
  imageDataUrl?: (src: string) => Promise<string | null>;
  /** Max output side and pixel count for PNG. */
  maxSide?: number;
  maxPixels?: number;
  /** Simplify tiny objects and text (thumbnails). */
  lod?: boolean;
  /** SVG: @font-face rules embedding the fonts the text uses, so the file looks the same elsewhere. */
  fontCss?: (usage: { family: string; bold: boolean; italic: boolean; text: string }[]) => Promise<string>;
}

export function collectExportItems(scene: Scene, opts: Pick<ExportOptions, 'ids' | 'bounds' | 'unavailable' | 'isUnavailable' | 'padding'>): { items: RenderItem[]; bounds: Rect } | null {
  const doc = scene.doc;
  let items: RenderItem[];
  let bounds = opts.bounds ?? null;
  if (opts.ids) {
    const leaves = commands.withContents(doc, opts.ids).filter((id) => doc.get(id)?.type !== CoreTypes.group && !scene.isFolded(id));
    items = leaves
      .map((id) => doc.get(id)!)
      .filter(Boolean)
      .map((obj) => ({ id: obj.id, obj, rev: scene.revOf(obj.id), prims: scene.primitives(obj), clip: scene.clipOf(obj) }))
      .sort((a, b) => scene.rankOf(a.id) - scene.rankOf(b.id));
  } else if (bounds) items = scene.visible(bounds);
  else {
    const total = scene.index.totalBounds();
    if (!total) return null;
    items = scene.visible(total);
  }
  if (opts.unavailable === 'skip') items = items.filter((it) => !opts.isUnavailable(it.obj));
  if (!bounds) {
    // what is actually drawn: text running out of its box, strokes and arrowheads included
    bounds = unionRects(items.map((it) => itemExtent(it, scene.index.bounds(it.id))));
    if (!bounds) return null;
  }
  const pad = opts.padding ?? 0;
  bounds = { x: bounds.x - pad, y: bounds.y - pad, w: bounds.w + pad * 2, h: bounds.h + pad * 2 };
  return { items, bounds };
}

function unionRects(rects: (Rect | null)[]): Rect | null {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const r of rects) {
    if (!r) continue;
    x0 = Math.min(x0, r.x);
    y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w);
    y1 = Math.max(y1, r.y + r.h);
  }
  return x1 >= x0 && y1 >= y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

function pathWidth(p: Extract<Primitive, { k: 'path' }>): number {
  const sw = p.stroke && p.stroke.width > 0 ? p.stroke.width : 0;
  return p.widths?.length ? Math.max(sw, ...p.widths) : sw;
}

/** Object-local box of a primitive as drawn (ink of text lines, half the stroke, miter joins, arrowheads). */
function primitiveBox(p: Primitive): Rect | null {
  switch (p.k) {
    case 'shape': {
      const sw = p.stroke && p.stroke.width > 0 ? p.stroke.width : 0;
      // sharp corners of triangles and diamonds stick out up to the miter limit
      const pad = p.shape === 'triangle' || p.shape === 'diamond' ? sw * 2 : sw / 2;
      return { x: p.x - pad, y: p.y - pad, w: p.w + pad * 2, h: p.h + pad * 2 };
    }
    case 'path': {
      if (p.points.length < 2) return null;
      const sw = pathWidth(p);
      const pts = [...p.points];
      for (const atEnd of [false, true]) {
        if (!p.stroke || p.closed || !(atEnd ? p.arrowEnd : p.arrowStart)) continue;
        const h = arrowHead(p.points, atEnd, p.stroke.width);
        if (h) pts.push(...h.tri);
      }
      const b = unionRects(pairsToRects(pts))!;
      const pad = p.round ? sw / 2 : sw * 2;
      return { x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 };
    }
    case 'text': {
      if (!p.text) return null;
      const fs = p.style.fontSize;
      const lay = layoutText(p.text, p.style, p.w, p.h);
      return unionRects(lay.lines.filter((l) => l.text).map((l) => ({ x: p.x + l.x - fs * 0.15, y: p.y + l.y - fs * 1.05, w: l.width + fs * 0.3, h: fs * 1.4 })));
    }
    case 'image':
    case 'raster':
      return { x: p.x, y: p.y, w: p.w, h: p.h };
  }
}

function pairsToRects(pts: number[]): Rect[] {
  const out: Rect[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) out.push({ x: pts[i], y: pts[i + 1], w: 0, h: 0 });
  return out;
}

/** World rect an item paints: its primitives turned with the object, within its frame clip. */
export function itemExtent(it: RenderItem, indexRect: Rect | null = null): Rect | null {
  const o = it.obj;
  const local = unionRects(it.prims.map(primitiveBox));
  let r: Rect | null = null;
  if (local) {
    const c = Math.cos(o.rotation);
    const s = Math.sin(o.rotation);
    const cx = o.x + o.w / 2;
    const cy = o.y + o.h / 2;
    const corners: Rect[] = [];
    for (const [lx, ly] of [
      [local.x, local.y],
      [local.x + local.w, local.y],
      [local.x, local.y + local.h],
      [local.x + local.w, local.y + local.h],
    ]) {
      const dx = lx - o.w / 2;
      const dy = ly - o.h / 2;
      corners.push({ x: cx + dx * c - dy * s, y: cy + dx * s + dy * c, w: 0, h: 0 });
    }
    r = unionRects(corners);
  }
  r = unionRects([r, indexRect]);
  if (!r || !it.clip) return r;
  const x0 = Math.max(r.x, it.clip.x);
  const y0 = Math.max(r.y, it.clip.y);
  const x1 = Math.min(r.x + r.w, it.clip.x + it.clip.w);
  const y1 = Math.min(r.y + r.h, it.clip.y + it.clip.h);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

async function preloadImages(items: RenderItem[], images: ImageProvider): Promise<void> {
  const srcs = new Set<string>();
  for (const it of items) for (const p of it.prims) if (p.k === 'image') srcs.add(p.src);
  for (let attempt = 0; attempt < 50; attempt++) {
    let missing = 0;
    for (const s of srcs) if (!images.get(s)) missing++;
    if (!missing) return;
    await new Promise((r) => setTimeout(r, 40));
  }
}

export async function exportPng(scene: Scene, opts: ExportOptions): Promise<Blob> {
  const canvas = await renderExport(scene, opts);
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/png' });
  return new Promise((resolve, reject) => (canvas as HTMLCanvasElement).toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'));
}

async function renderExport(scene: Scene, opts: ExportOptions): Promise<OffscreenCanvas | HTMLCanvasElement> {
  const col = collectExportItems(scene, opts);
  if (!col) throw new Error('Nothing to export');
  const { items, bounds } = col;
  const scale = opts.scale ?? 1;
  const W = Math.max(1, Math.ceil(bounds.w * scale));
  const H = Math.max(1, Math.ceil(bounds.h * scale));
  const maxSide = opts.maxSide ?? 16384;
  const maxPixels = opts.maxPixels ?? 128 * 1024 * 1024;
  if (W > maxSide || H > maxSide || W * H > maxPixels) throw new ExportTooLargeError(W, H);
  await preloadImages(items, opts.images);
  const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(W, H) : Object.assign(document.createElement('canvas'), { width: W, height: H });
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  if (opts.background) {
    ctx.fillStyle = cssColor(opts.background);
    ctx.fillRect(0, 0, W, H);
  }
  ctx.save();
  for (const it of items) {
    const o = it.obj;
    // frame clipping
    ctx.restore();
    ctx.save();
    if (it.clip) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.beginPath();
      ctx.rect((it.clip.x - bounds.x) * scale, (it.clip.y - bounds.y) * scale, it.clip.w * scale, it.clip.h * scale);
      ctx.clip();
    }
    if (opts.lod && Math.max(o.w, o.h) * scale < 2) {
      // thumbnails: sub-2px objects become a single dot
      const p0 = it.prims[0];
      const color = p0?.k === 'shape' ? (p0.fill.kind !== 'none' ? p0.fill.color : p0.stroke?.color) : p0?.k === 'path' ? p0.stroke?.color : '#cbd5e1';
      if (color) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = o.style.opacity ?? 1;
        ctx.fillStyle = cssColor(color);
        ctx.fillRect((o.x - bounds.x) * scale, (o.y - bounds.y) * scale, Math.max(1, o.w * scale), Math.max(1, o.h * scale));
      }
      continue;
    }
    const c = Math.cos(o.rotation);
    const s = Math.sin(o.rotation);
    const cx = (o.x + o.w / 2 - bounds.x) * scale;
    const cy = (o.y + o.h / 2 - bounds.y) * scale;
    ctx.setTransform(scale * c, scale * s, -scale * s, scale * c, cx, cy);
    ctx.translate(-o.w / 2, -o.h / 2);
    for (const p of it.prims) drawPrimitive(ctx, p, o.style.opacity ?? 1, scale, { images: opts.images, textLodPx: opts.lod ? 3 : 0 });
  }
  return canvas;
}

// ---------------- SVG ----------------

const n = (v: number) => (Math.abs(v) < 1e-9 ? '0' : (+v.toFixed(3)).toString());

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function colorAttrs(c: string, kind: 'fill' | 'stroke', alpha = 1): string {
  const [r, g, b, a] = parseColor(c);
  const hex = '#' + [r, g, b].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
  const op = a * alpha;
  return `${kind}="${hex}"${op < 1 ? ` ${kind}-opacity="${n(op)}"` : ''}`;
}

class SvgBuilder {
  defs: string[] = [];
  body: string[] = [];
  private idc = 0;

  id(prefix: string): string {
    return `${prefix}${++this.idc}`;
  }

  fillAttr(fill: Fill): string {
    switch (fill.kind) {
      case 'none':
        return 'fill="none"';
      case 'solid':
        return colorAttrs(fill.color, 'fill');
      case 'lines':
      case 'grid': {
        const sp = patternSpacing(fill);
        const th = patternThickness(sp);
        const id = this.id('pat');
        const bg = fill.background ? `<rect width="${n(sp)}" height="${n(sp)}" ${colorAttrs(fill.background, 'fill')}/>` : '';
        const d = fill.kind === 'lines' ? `M0,0V${n(sp)}M${n(sp)},0V${n(sp)}` : `M0,0V${n(sp)}M${n(sp)},0V${n(sp)}M0,0H${n(sp)}M0,${n(sp)}H${n(sp)}`;
        const rot = fill.kind === 'lines' ? ` patternTransform="rotate(${n(((fill.angle ?? Math.PI / 4) * 180) / Math.PI)})"` : '';
        this.defs.push(
          `<pattern id="${id}" patternUnits="userSpaceOnUse" width="${n(sp)}" height="${n(sp)}"${rot}>${bg}<path d="${d}" ${colorAttrs(fill.color, 'stroke')} stroke-width="${n(th)}"/></pattern>`,
        );
        return `fill="url(#${id})"`;
      }
    }
  }

  strokeAttr(stroke: Stroke | null): string {
    if (!stroke || stroke.width <= 0) return 'stroke="none"';
    const dash = dashPattern(stroke.dash, stroke.width);
    return [
      colorAttrs(stroke.color, 'stroke'),
      `stroke-width="${n(stroke.width)}"`,
      dash.length ? `stroke-dasharray="${dash.map(n).join(' ')}"` : '',
      stroke.dash === 'dotted' ? 'stroke-linecap="round"' : '',
      'stroke-miterlimit="4"',
    ]
      .filter(Boolean)
      .join(' ');
  }
}

async function primitiveSvg(b: SvgBuilder, p: Primitive, opts: ExportOptions): Promise<string> {
  const op = p.opacity !== undefined && p.opacity < 1 ? ` opacity="${n(p.opacity)}"` : '';
  switch (p.k) {
    case 'shape': {
      const fs = `${b.fillAttr(p.fill)} ${b.strokeAttr(p.stroke)}${op}`;
      if (p.shape === 'rect') {
        const r = Math.min(p.radius ?? 0, p.w / 2, p.h / 2);
        return `<rect x="${n(p.x)}" y="${n(p.y)}" width="${n(p.w)}" height="${n(p.h)}"${r > 0 ? ` rx="${n(r)}"` : ''} ${fs}/>`;
      }
      if (p.shape === 'ellipse') return `<ellipse cx="${n(p.x + p.w / 2)}" cy="${n(p.y + p.h / 2)}" rx="${n(p.w / 2)}" ry="${n(p.h / 2)}" ${fs}/>`;
      const pts =
        p.shape === 'triangle'
          ? [p.x + p.w / 2, p.y, p.x + p.w, p.y + p.h, p.x, p.y + p.h]
          : [p.x + p.w / 2, p.y, p.x + p.w, p.y + p.h / 2, p.x + p.w / 2, p.y + p.h, p.x, p.y + p.h / 2];
      return `<polygon points="${pairs(pts)}" ${fs}/>`;
    }
    case 'path': {
      const out: string[] = [];
      const sw = p.stroke?.width ?? 1;
      const mix = p.blend === 'highlight' ? ' style="mix-blend-mode:darken"' : '';
      if (p.closed && p.holes?.length) {
        const ring = (r: number[]) => 'M' + pairs(r).replace(/ /g, ' L') + ' Z';
        out.push(`<path d="${[p.points, ...p.holes].map(ring).join(' ')}" fill-rule="evenodd" ${b.fillAttr(p.fill)} ${b.strokeAttr(p.stroke)} stroke-linejoin="miter"${op}${mix}/>`);
      } else if (p.closed) out.push(`<polygon points="${pairs(p.points)}" ${b.fillAttr(p.fill)} ${b.strokeAttr(p.stroke)} stroke-linejoin="miter"${op}${mix}/>`);
      else if (p.stroke && p.widths && p.widths.length === p.points.length / 2) {
        const raw: number[] = [];
        strokeVariable(p.points, p.widths, false, raw);
        if (p.round) roundStroke(p.points, p.widths, raw);
        // one path, one winding: the ribbon is painted once, so see-through strokes have no darker joins
        const tris = orientTris(raw);
        let d = '';
        for (let i = 0; i < tris.length; i += 6) d += `M${n(tris[i])},${n(tris[i + 1])}L${n(tris[i + 2])},${n(tris[i + 3])}L${n(tris[i + 4])},${n(tris[i + 5])}Z`;
        if (d) out.push(`<path d="${d}" ${colorAttrs(p.stroke.color, 'fill')} stroke="none"${op}${mix}/>`);
      } else {
        const pts = trimForArrows(p.points, !!p.arrowStart, !!p.arrowEnd, sw);
        out.push(`<polyline points="${pairs(pts)}" fill="none" ${b.strokeAttr(p.stroke)} ${p.round ? 'stroke-linejoin="round" stroke-linecap="round"' : 'stroke-linejoin="miter"'}${op}${mix}/>`);
        if (p.stroke) {
          for (const atEnd of [false, true]) {
            if (!(atEnd ? p.arrowEnd : p.arrowStart)) continue;
            const h = arrowHead(p.points, atEnd, sw);
            if (h) out.push(`<polygon points="${pairs(h.tri)}" ${colorAttrs(p.stroke.color, 'fill')}${op}${mix}/>`);
          }
        }
      }
      return out.join('');
    }
    case 'text': {
      const layout = layoutText(p.text, p.style, p.w, p.h);
      const st = p.style;
      const attrs = [
        `font-family="${esc(st.fontFamily ?? DEFAULT_FONT_FAMILY)}"`,
        `font-size="${n(st.fontSize)}"`,
        st.bold ? 'font-weight="bold"' : '',
        st.italic ? 'font-style="italic"' : '',
        colorAttrs(st.color, 'fill'),
      ]
        .filter(Boolean)
        .join(' ');
      const lines = layout.lines
        .filter((l) => l.text)
        .map((l) => `<text x="${n(p.x + l.x)}" y="${n(p.y + l.y)}" ${attrs} xml:space="preserve">${esc(l.text)}</text>`);
      return lines.length ? `<g${op}>${lines.join('')}</g>` : '';
    }
    case 'image': {
      const url = opts.imageDataUrl ? await opts.imageDataUrl(p.src) : null;
      if (!url) return `<rect x="${n(p.x)}" y="${n(p.y)}" width="${n(p.w)}" height="${n(p.h)}" fill="#e5e7eb"/>`;
      return `<image x="${n(p.x)}" y="${n(p.y)}" width="${n(p.w)}" height="${n(p.h)}" href="${url}" preserveAspectRatio="none"${op}/>`;
    }
    case 'raster': {
      // modes B/C are exported as raster inserts (§7.3)
      if (!p.bitmap) return '';
      const url = await bitmapToDataUrl(p.bitmap);
      return `<image x="${n(p.x)}" y="${n(p.y)}" width="${n(p.w)}" height="${n(p.h)}" href="${url}" preserveAspectRatio="none"${op}/>`;
    }
  }
}

function pairs(pts: number[]): string {
  const out: string[] = [];
  for (let i = 0; i < pts.length; i += 2) out.push(n(pts[i]) + ',' + n(pts[i + 1]));
  return out.join(' ');
}

async function bitmapToDataUrl(bmp: ImageBitmap): Promise<string> {
  const c = new OffscreenCanvas(bmp.width, bmp.height);
  c.getContext('2d')!.drawImage(bmp, 0, 0);
  const blob = await c.convertToBlob({ type: 'image/png' });
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return 'data:image/png;base64,' + btoa(bin);
}

export async function exportSvg(scene: Scene, opts: ExportOptions): Promise<string> {
  const col = collectExportItems(scene, opts);
  if (!col) throw new Error('Nothing to export');
  const { items, bounds } = col;
  const b = new SvgBuilder();
  const clipIds = new Map<string, string>();
  for (const it of items) {
    const o = it.obj;
    const cx = o.x + o.w / 2 - bounds.x;
    const cy = o.y + o.h / 2 - bounds.y;
    const rot = o.rotation ? ` rotate(${n((o.rotation * 180) / Math.PI)})` : '';
    const opacity = (o.style.opacity ?? 1) < 1 ? ` opacity="${n(o.style.opacity)}"` : '';
    const parts: string[] = [];
    for (const p of it.prims) parts.push(await primitiveSvg(b, p, opts));
    const group = `<g data-id="${esc(o.id)}" transform="translate(${n(cx)},${n(cy)})${rot} translate(${n(-o.w / 2)},${n(-o.h / 2)})"${opacity}>${parts.join('')}</g>`;
    if (it.clip) {
      // frame clipping: the clip path lives in root user space, so it wraps the transformed group
      const c = it.clip;
      const key = `${c.x},${c.y},${c.w},${c.h}`;
      let id = clipIds.get(key);
      if (!id) {
        id = b.id('clip');
        clipIds.set(key, id);
        b.defs.push(`<clipPath id="${id}" clipPathUnits="userSpaceOnUse"><rect x="${n(c.x - bounds.x)}" y="${n(c.y - bounds.y)}" width="${n(c.w)}" height="${n(c.h)}"/></clipPath>`);
      }
      b.body.push(`<g clip-path="url(#${id})">${group}</g>`);
    } else b.body.push(group);
  }
  if (opts.fontCss) {
    // one entry per font / weight / style with all the characters drawn in it
    const usage = new Map<string, { family: string; bold: boolean; italic: boolean; text: string }>();
    for (const it of items) {
      for (const p of it.prims) {
        if (p.k !== 'text' || !p.text) continue;
        const family = p.style.fontFamily ?? DEFAULT_FONT_FAMILY;
        const key = family + '|' + !!p.style.bold + '|' + !!p.style.italic;
        const u = usage.get(key) ?? { family, bold: !!p.style.bold, italic: !!p.style.italic, text: '' };
        u.text += p.text;
        usage.set(key, u);
      }
    }
    const css = usage.size ? await opts.fontCss([...usage.values()]) : '';
    if (css) b.defs.unshift(`<style>${css}</style>`);
  }
  const W = n(bounds.w);
  const H = n(bounds.h);
  // the drawing stays in world units (viewBox); `scale` sets the size the file opens at
  const scale = opts.scale ?? 1;
  const bg = opts.background ? `<rect width="100%" height="100%" ${colorAttrs(opts.background, 'fill')}/>` : '';
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${n(bounds.w * scale)}" height="${n(bounds.h * scale)}" viewBox="0 0 ${W} ${H}">` +
    (b.defs.length ? `<defs>${b.defs.join('')}</defs>` : '') +
    bg +
    b.body.join('') +
    `</svg>`
  );
}
